<template>
  <div class="add-dish-form-container slide-up-form" :class="{ show: show, hiding: isHiding }">
    <form id="add-dish-form" @submit.prevent="handleForm">
      <div>
        <p>Add Dish</p>
        <button type="button" @click="hideForm" class="button secondary" id="close_form_button">
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
      </div>
      <label>Dish Name</label>
      <input maxlength="50" v-model="title" placeholder="e.g. Pasta" type="text" />
      <label>List</label>
      <DishListSelector :modelValue="list" @list-change="dishListSelectChangeHandler" />
      <label>Description (optional)</label>
      <textarea maxlength="200" v-model="description" placeholder="e.g. A simple pasta dish with tomato sauce"></textarea>
      <label>Exposure Goal</label>
      <input min="1" max="99" v-model.number="goal" placeholder="e.g. 12" type="number" />
      <button class="button primary">
        <span v-if="!loading">Add Dish</span>
        <span v-if="loading" class="loader button-loader"></span>
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { fetchWithAuth } from '@/utils/api';
import { type Dish } from '@/types';
import DishListSelector from './DishListSelector.vue';

const props = defineProps<{ show: boolean }>();

const emit = defineEmits<{
  (e: 'hideForm'): void;
  (e: 'dishAdded', newDish: Dish): void;
}>();

const store = useStore();
const apiUrl = computed(() => store.getters.getApiUrl);

// Form fields
const title = ref('');
const goal = ref<number | null>(null);
const description = ref('');
const list = ref<string>('current'); // Default to 'current' list
const loading = ref(false);

// Hide form
const hideForm = () => {
  emit('hideForm');
};

const dishListSelectChangeHandler = (listId: string) => {
  list.value = listId;
};

// Reset form
const resetForm = () => {
  title.value = '';
  goal.value = null;
  description.value = '';
  list.value = 'current';
};

const handleForm = async () => {
  if (!title.value || goal.value === null) {
    alert('Please fill in both title and goal fields before submitting.');
    return;
  }

  try {
    loading.value = true;
    const response = await fetchWithAuth(
      `${apiUrl.value}/api/dishes`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: title.value,
          goal: goal.value,
          description: description.value,
          list: list.value,
        }),
      },
      store
    );

    if (response.ok) {
      const response_json = await response.json();
      const dishId = Number(response_json.dishId);

      const newDish = {
        id: dishId,
        title: title.value,
        description: description.value,
        goal: goal.value,
        list: list.value,
        logs: [],
      };

      emit('dishAdded', newDish);
      resetForm();
    } else {
      const errorData = await response.json().catch(() => ({}));
      alert(`Failed to add dish: ${errorData.error || 'Unknown error'}`);
    }
    loading.value = false;
  } catch (error) {
    loading.value = false;
    console.error('An error occurred while adding the dish:', error);
    alert('An unexpected error occurred. Please try again later.');
  }
};

const isHiding = ref(false);

watch(() => props.show, (newVal) => {
  if (!newVal) {
    isHiding.value = true;
    setTimeout(() => {
      isHiding.value = false;
    }, 300); // Match your transition time (0.3s)
  }
});
</script>

<style>
.add-dish-form-container {
  width: 100vw;
  height: 0;
  background-color: white;
  position: fixed;
  bottom: 0;
  left: 0;
  overflow: hidden;
  transition: height 0.3s ease;
  pointer-events: none;
  opacity: 0;
  overflow-y: scroll;
}

.add-dish-form-container.show {
  height: 100vh;
  opacity: 1;
  pointer-events: auto;
}

.add-dish-form-container.hiding {
  height: 0;
  opacity: 0;
  pointer-events: none;
}

#add-dish-form {
  width: 100%;
  max-width: 500px;
  padding: 25px 10px 0px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0 auto;
}

#add-dish-form > div:nth-of-type(1) {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
}

#add-dish-form > div:nth-of-type(1) > p {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

#add-dish-form > div:nth-of-type(1) > button {
  background-color: transparent;
}

#add-dish-form textarea {
  width: 100%;
  height: 120px;
  resize: none;
  padding: 13px;
}

#add-dish-form input,
#add-dish-form textarea {
  margin-bottom: 10px;
}

#close_form_button {
  padding: var(--padding-sm);
  border-radius: 5px;
  width: 40px;
  height: 40px;
}

#close_form_button:hover {
  background-color: hsl(28, 100%, 96%) !important;
}
</style>
